import { i18n } from '../i18n.js';
/**
 * Tutorial Cue Layer
 * Body-mounted overlay that renders the tutorial's attention cues:
 * click halos, drag comets and "look here" shimmers, plus a darkening
 * veil with soft holes around the elements that matter.
 *
 * Two layers because they need different depths:
 *  - #tutorial-veil-layer  (z 9400) darkens the scene below spell
 *    announcements (9600), tips (10000) and the drag clone (15000).
 *    Hover-enlarged cards CANNOT rise above it (their z 9500 is trapped
 *    inside lower hand/board stacking contexts), so while any tracked
 *    anchor is hover-zoomed the veil softens instead.
 *  - #tutorial-cue-layer   (z 9998) carries the rings/pills/comets.
 *
 * Everything here is pointer-events:none and purely visual; move gating
 * stays in tutorial.css + the authoritative server.
 */

import { Platform } from './platform.js';
import { SafeArea } from './safe-area.js';
import { GameState } from '../game-state.js';
import { uiPx } from './ui-scale.js';
let TutorialCues = {};

TutorialCues = (function () {
    'use strict';

    const SVG_NS = 'http://www.w3.org/2000/svg';
    const COMET_PERIOD_MS = 2400;
    const COMET_TRAIL_FRACTION = 0.22;
    const INSIST_AFTER_MS = 4000;

    let cueLayer = null;
    let veilLayer = null;
    let veilSvg = null;
    let veilMask = null;
    let veilHoles = [];
    let veilAnchors = [];
    let veilActive = false;
    // When the current veil target set was applied — the clock the readiness
    // cap is measured against, mirroring each cue entry's own `appliedAt`.
    let veilAppliedAt = 0;
    let veilKey = '';
    let rafHandle = 0;
    let resizeHandler = null;
    let localizationHandler = null;
    let motionQuery = null;
    let motionHandler = null;
    const cueMap = new Map();
    // Per-frame rect memo (ring + veil hole share one measurement) and
    // write-avoidance caches — idle frames then cost almost nothing.
    const frameRects = new Map();
    const framePaint = new Map();
    // Text ornaments are laid out in cue insertion order each frame. Reserve
    // every accepted label/chevron box so the next forced card cannot paint an
    // identical Tap/Drag label over it on compact mulligan and battle rows.
    const frameOrnamentBoxes = [];
    const lastBoxes = new WeakMap();
    const radiusCache = new WeakMap();
    // Only fanned hand/board cards ever carry transforms worth reading.
    const TRANSFORM_CONTAINERS = new Set(['player-hand', 'player-board', 'opponent-board']);

    function reducedMotionQuery() {
        try {
            return typeof window.matchMedia === 'function'
                ? window.matchMedia('(prefers-reduced-motion: reduce)')
                : null;
        } catch (err) {
            return null;
        }
    }

    function vminPx(units) {
        return (Math.min(window.innerWidth, window.innerHeight) / 100) * units;
    }

    function uiLabel(key, fallback) {
        const translated = i18n?.t?.(key);
        return translated && translated !== key ? translated : fallback;
    }

    function now() {
        return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    }

    /**
     * Cards are re-created on every board render, so cue anchors are stored
     * as "how to find this element again", not as a node reference.
     */
    function makeAnchorRef(el) {
        const ref = {
            el,
            instanceId: el.dataset?.instanceId || null,
            cardId: el.dataset?.cardId || null,
            id: el.id || null,
        };
        const container = el.closest?.('#player-hand, #player-board, #opponent-board, #mulligan-cards');
        ref.containerId = container ? container.id : null;
        return ref;
    }

    function resolveAnchor(ref) {
        if (!ref) return null;
        if (ref.el && ref.el.isConnected) return ref.el;
        const scope = (ref.containerId && document.getElementById(ref.containerId)) || document;
        if (ref.instanceId) {
            const found = scope.querySelector(`[data-instance-id="${ref.instanceId}"]`);
            if (found) return (ref.el = found);
        }
        if (ref.cardId) {
            const found = scope.querySelector(`[data-card-id="${ref.cardId}"]`);
            if (found) return (ref.el = found);
        }
        if (ref.id) {
            const found = document.getElementById(ref.id);
            // Hand ids are positional (player-hand-N names a slot, not a card),
            // so an id-only match is trusted only for non-card anchors.
            if (found && !ref.instanceId && !ref.cardId) return (ref.el = found);
        }
        return null;
    }

    function anchorKey(el) {
        return el.dataset?.instanceId || el.dataset?.cardId || el.id || 'anon';
    }

    function cueKey(spec) {
        if (spec.type === 'drag') {
            return `drag:${anchorKey(spec.from)}->${anchorKey(spec.to)}`;
        }
        return `${spec.type}:${anchorKey(spec.anchor)}`;
    }

    /** Rotation + scale of the anchor so rings sit on fanned hand cards. */
    function anchorTransform(el) {
        try {
            const matrix = window.getComputedStyle(el).transform || '';
            const parsed = matrix.match(/matrix\(([^)]+)\)/);
            if (!parsed) return { angle: 0, scaleX: 1, scaleY: 1 };
            const [a, b, c, d] = parsed[1].split(',').map(parseFloat);
            return {
                angle: Math.atan2(b, a) * (180 / Math.PI),
                scaleX: Math.hypot(a, b) || 1,
                scaleY: Math.hypot(c, d) || 1,
            };
        } catch (err) {
            return { angle: 0, scaleX: 1, scaleY: 1 };
        }
    }

    /**
     * The ring copies the anchor's real corner shape — the opponent portrait,
     * for example, is a rounded-top rectangle, not a circle, and an assumed
     * shape reads as a sloppy hitbox.
     */
    function anchorRadius(el) {
        if (radiusCache.has(el)) return radiusCache.get(el);
        let radius = null;
        try {
            const computed = window.getComputedStyle(el).borderRadius || '';
            if (computed && computed !== '0px') radius = computed;
        } catch (err) {
            radius = null;
        }
        radiusCache.set(el, radius);
        return radius;
    }

    function measureRect(el) {
        let rect = frameRects.get(el);
        if (!rect) {
            rect = el.getBoundingClientRect();
            frameRects.set(el, rect);
        }
        return rect;
    }

    function validRect(rect) {
        return (rect.width || rect.height) ? rect : null;
    }

    /* A CUE MAY NOT RING EMPTY SPACE.

       Reported: "pls make highlights in tutorial only appear when the cards are
       actually there. e.g. in the beginning the cards to play are 'highlighted'
       but it takes ~1s to actually draw em, until then empty space is
       highlighted."

       `validRect` asked only whether the anchor had width, and an anchor that
       is not painted still has width. Two of them arrive that way: `FX.drawCard`
       holds each live hand card at `visibility: hidden` (class `fx-draw-hold`)
       for its clone's whole flight — up to 85ms of stagger per card plus 380ms
       of travel — and the mulligan's own cards fade in after their overlay. So
       the ring painted over a seat with nothing in it. Measured on the guest
       tutorial in Chromium: a ring sat over an empty `#mulligan-overlay` from
       1611ms to 2100ms of page life.

       Keyed on PAINT rather than on `fx-draw-hold`, because the class is only
       one of the ways an anchor is not there yet, and a check that names one
       mechanism stops working the moment a second appears. */
    function anchorPainted(el) {
        if (!el || !el.isConnected) return false;
        if (framePaint.has(el)) return framePaint.get(el);
        let painted = true;
        for (let node = el; node && node !== document.documentElement; node = node.parentElement) {
            const style = window.getComputedStyle(node);
            if (style.visibility === 'hidden' || style.display === 'none') {
                painted = false;
                break;
            }
            /* NO INFORMATION IS NOT ZERO. `Number('')` is 0, and an engine that
               reports no computed opacity — jsdom does, for every element —
               would make this gate hide every cue that ever existed. Only a
               value that actually parses may hide anything. */
            const opacity = Number.parseFloat(style.opacity);
            if (Number.isFinite(opacity) && opacity < 0.05) {
                painted = false;
                break;
            }
        }
        framePaint.set(el, painted);
        return painted;
    }

    /* ...AND A CUE MAY NOT BE DELETED BY THAT GATE EITHER.

       This is a readiness gate, so it carries a hard cap: past
       ANCHOR_PAINT_CAP_MS the cue appears regardless. An anchor that never
       becomes visible — an art failure, a lane that stays hidden — must still
       get its instruction, or a warm-up has silently removed the tutorial. */
    const ANCHOR_PAINT_CAP_MS = 1500;

    function anchorReady(entry, el, ts) {
        if (anchorPainted(el)) return true;
        return (ts - entry.appliedAt) >= ANCHOR_PAINT_CAP_MS;
    }

    function ensureLayers() {
        if (!veilLayer) {
            veilLayer = document.createElement('div');
            veilLayer.id = 'tutorial-veil-layer';
            veilLayer.setAttribute('aria-hidden', 'true');
            veilLayer.style.pointerEvents = 'none';

            veilSvg = document.createElementNS(SVG_NS, 'svg');
            veilSvg.setAttribute('class', 'cue-veil');
            veilSvg.setAttribute('width', '100%');
            veilSvg.setAttribute('height', '100%');

            const defs = document.createElementNS(SVG_NS, 'defs');
            const gradient = document.createElementNS(SVG_NS, 'radialGradient');
            gradient.setAttribute('id', 'cue-veil-hole-gradient');
            [['52%', '#000'], ['82%', '#777'], ['100%', '#fff']].forEach(([offset, color]) => {
                const stop = document.createElementNS(SVG_NS, 'stop');
                stop.setAttribute('offset', offset);
                stop.setAttribute('stop-color', color);
                gradient.appendChild(stop);
            });
            defs.appendChild(gradient);

            veilMask = document.createElementNS(SVG_NS, 'mask');
            veilMask.setAttribute('id', 'cue-veil-mask');
            const keep = document.createElementNS(SVG_NS, 'rect');
            keep.setAttribute('fill', '#fff');
            keep.setAttribute('x', '0');
            keep.setAttribute('y', '0');
            keep.setAttribute('width', '100%');
            keep.setAttribute('height', '100%');
            veilMask.appendChild(keep);
            defs.appendChild(veilMask);
            veilSvg.appendChild(defs);

            const scrim = document.createElementNS(SVG_NS, 'rect');
            scrim.setAttribute('class', 'cue-veil-scrim');
            scrim.setAttribute('x', '0');
            scrim.setAttribute('y', '0');
            scrim.setAttribute('width', '100%');
            scrim.setAttribute('height', '100%');
            scrim.setAttribute('mask', 'url(#cue-veil-mask)');
            veilSvg.appendChild(scrim);

            veilLayer.appendChild(veilSvg);
            document.body.appendChild(veilLayer);
        }

        if (!cueLayer) {
            cueLayer = document.createElement('div');
            cueLayer.id = 'tutorial-cue-layer';
            cueLayer.className = 'tutorial-cue-layer';
            cueLayer.setAttribute('aria-hidden', 'true');
            cueLayer.style.pointerEvents = 'none';

            // Track the OS motion preference live — players may toggle it
            // mid-session and the comet is JS-driven, beyond @media's reach.
            motionQuery = reducedMotionQuery();
            if (motionQuery?.matches) {
                cueLayer.classList.add('cue-layer--static');
            }
            if (motionQuery && typeof motionQuery.addEventListener === 'function') {
                motionHandler = (event) => {
                    cueLayer?.classList.toggle('cue-layer--static', event.matches);
                };
                motionQuery.addEventListener('change', motionHandler);
            }

            document.body.appendChild(cueLayer);

            /* NOT coalesced, deliberately. Deferring this by a frame looks
               like a free optimisation — one language switch runs
               applyTranslations several times — but the re-measure is
               synchronous ON PURPOSE: a crowded prior frame may have hidden the
               pill, and the handler reveals it, measures it, and repositions it
               in the same tick. Defer it and a zero offsetWidth caches the old
               compact fallback for the wider translation. Locked by
               tutorialCues.test.js. */
            resizeHandler = refreshCueGeometry;
            localizationHandler = refreshCueGeometry;
            window.addEventListener('resize', resizeHandler, { passive: true });
            window.addEventListener('moonfall:viewport-change', resizeHandler);
            window.addEventListener('moonfall:languagechange', localizationHandler);
            document.addEventListener('moonfall:i18n-translated', localizationHandler);
        }
    }

    function makeRing(extraClass = '') {
        const ring = document.createElement('div');
        ring.className = `cue-ring${extraClass ? ` ${extraClass}` : ''}`;
        return ring;
    }

    function makeChevron() {
        const svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('class', 'cue-chevron');
        svg.setAttribute('viewBox', '0 0 24 14');
        const glyph = document.createElementNS(SVG_NS, 'path');
        glyph.setAttribute('d', 'M2 2 L12 11 L22 2');
        glyph.setAttribute('fill', 'none');
        glyph.setAttribute('stroke-width', '3.4');
        glyph.setAttribute('stroke-linecap', 'round');
        glyph.setAttribute('stroke-linejoin', 'round');
        svg.appendChild(glyph);
        return svg;
    }

    function makePill(labelKey, fallback) {
        const pill = document.createElement('div');
        pill.className = 'cue-pill';
        pill.textContent = uiLabel(labelKey, fallback);
        pill.setAttribute('data-i18n', labelKey);
        return pill;
    }

    function syncInteractionLabel(entry) {
        if (!entry?.spec || (entry.spec.type !== 'click' && entry.spec.type !== 'drag')) return;
        const pill = entry.node.querySelector('.cue-pill');
        if (!pill) return;
        const mobileUi = entry.spec.type === 'click' && !!Platform?.isMobileUi?.();
        const key = entry.spec.type === 'drag'
            ? 'game.tutorialCueDrag'
            : (mobileUi ? 'game.tutorialCueTap' : 'game.tutorialCueClick');
        const fallback = entry.spec.type === 'drag' ? 'Drag' : (mobileUi ? 'Tap' : 'Click');
        const nextText = uiLabel(key, fallback);
        const changed = pill.getAttribute('data-i18n') !== key || pill.textContent !== nextText;
        pill.setAttribute('data-i18n', key);
        pill.textContent = nextText;
        if (changed) {
            entry.pillW = 0;
            entry.pillH = 0;
            entry.measurePillVisible = true;
        }
    }

    function syncInteractionLabels() {
        cueMap.forEach(syncInteractionLabel);
    }

    function refreshCueGeometry() {
        syncInteractionLabels();
        cueMap.forEach((entry) => {
            entry.pillW = 0;
            entry.pillH = 0;
            entry.measurePillVisible = true;
        });
        positionAll();
    }

    function buildCueNode(spec) {
        const node = document.createElement('div');
        /* BORN HIDDEN: a ring is placed before it is ever shown. A node that
           entered visible had no box until positionAll measured its anchor —
           and that measurement forces a style pass, so the ring's first
           computed opacity was 1 at the layer's origin. The `cue--hidden` that
           followed for an anchor not painted yet then FADED a 2x2 ring in the
           corner out over 350 ms; an anchor that painted mid-fade moved and
           resized it onto the target. Round 13, guest tutorial mulligan: a
           flicker cold, a 615x465 px morph warm (baseline too). positionAll
           reveals the cue in the same pass that sets its box. */
        node.className = `cue cue--${spec.type} cue--hidden`;

        if (spec.type === 'click') {
            if (spec.ring !== false) {
                node.appendChild(makeRing());
            }
            node.appendChild(makeChevron());
            if (spec.pill !== false) {
                const mobileUi = !!Platform?.isMobileUi?.();
                node.appendChild(makePill(
                    mobileUi ? 'game.tutorialCueTap' : 'game.tutorialCueClick',
                    mobileUi ? 'Tap' : 'Click'
                ));
            }
        } else if (spec.type === 'look') {
            node.appendChild(makeRing('cue-ring--look'));
        } else if (spec.type === 'drag') {
            node.appendChild(makeRing('cue-ring--source'));
            /* `target: false` teaches the gesture without ringing a place: an
               untargeted Spell is cast by a release anywhere above the hand. */
            if (spec.target !== false) node.appendChild(makeRing('cue-ring--target'));

            const svg = document.createElementNS(SVG_NS, 'svg');
            svg.setAttribute('class', 'cue-path');
            const trail = document.createElementNS(SVG_NS, 'path');
            trail.setAttribute('class', 'cue-trail');
            trail.setAttribute('fill', 'none');
            svg.appendChild(trail);
            node.appendChild(svg);

            const comet = document.createElement('div');
            comet.className = 'cue-comet';
            node.appendChild(comet);

            if (spec.pill !== false) {
                node.appendChild(makePill('game.tutorialCueDrag', 'Drag'));
            }
        }

        return node;
    }

    /* Content bounds that keep clear of the notch / home indicator. Falls back
       to the plain viewport if scripts/ui/safe-area.js has not loaded. */
    function safeBox(margin) {
        const api = SafeArea;
        if (api?.box) return api.box(margin);
        return {
            left: margin, top: margin,
            right: window.innerWidth - margin, bottom: window.innerHeight - margin,
        };
    }

    function setBox(el, left, top, width, height) {
        const prev = lastBoxes.get(el);
        if (prev && Math.abs(prev.left - left) < 0.1 && Math.abs(prev.top - top) < 0.1
            && Math.abs(prev.width - width) < 0.1 && Math.abs(prev.height - height) < 0.1) {
            return;
        }
        lastBoxes.set(el, { left, top, width, height });
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
    }

    function positionRingOn(ring, target, containerId) {
        const rect = measureRect(target);
        if (!rect.width && !rect.height) return null;
        // Buttons, portraits and indicators never rotate — skip the
        // computed-style read for everything but fanned card containers.
        const { angle, scaleX, scaleY } = TRANSFORM_CONTAINERS.has(containerId || '')
            ? anchorTransform(target)
            : { angle: 0, scaleX: 1, scaleY: 1 };
        const pad = Math.max(6, vminPx(0.85));
        // Unrotated anchors: trust the rect, which also captures CSS `zoom`
        // (board hover) that never shows up in the transform matrix. Fanned
        // hand cards need offset*scale instead — their rect is inflated by
        // the rotation's bounding box.
        const rotated = Math.abs(angle) > 0.5;
        let baseW = rect.width;
        let baseH = rect.height;
        if (rotated) {
            baseW = (target.offsetWidth || rect.width / (scaleX || 1)) * scaleX;
            baseH = (target.offsetHeight || rect.height / (scaleY || 1)) * scaleY;
        }
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const w = baseW + pad * 2;
        const h = baseH + pad * 2;
        setBox(ring, cx - w / 2, cy - h / 2, w, h);
        ring.style.transform = rotated ? `rotate(${angle.toFixed(2)}deg)` : '';
        ring.style.borderRadius = anchorRadius(target) || `${Math.max(8, vminPx(1.2))}px`;
        return rect;
    }

    /** Hover-enlargement: hand/mulligan use classes, board hover uses CSS zoom. */
    function anchorHovered(el) {
        if (!el) return false;
        if (el.classList.contains('hover-active') || el.classList.contains('hover-enlarged')) return true;
        const zoom = parseFloat(el.style?.zoom);
        return Number.isFinite(zoom) && zoom > 1.001;
    }

    function pillSize(entry, pill) {
        if (!pill) return { pillW: 0, pillH: Math.max(18, vminPx(2.6)) };
        if (!entry.pillW || !entry.pillH) {
            // Collision handling may have hidden the pill on the prior frame.
            // Reveal only at the point of measurement (after the suspended
            // action gate) so localization cannot expose an overlapping label
            // while cues are intentionally frozen.
            if (entry.measurePillVisible) pill.style.display = '';
            // Measured once per cue (and re-measured after resize) — reading
            // offsetWidth mid-frame between style writes forces layout.
            entry.pillW = pill.offsetWidth || Math.max(52, vminPx(7));
            entry.pillH = pill.offsetHeight || Math.max(18, vminPx(2.6));
            entry.measurePillVisible = false;
        }
        return { pillW: entry.pillW, pillH: entry.pillH };
    }

    function positionClickCue(entry, ts) {
        const target = resolveAnchor(entry.anchorRef);
        if (!target) return false;
        if (!anchorReady(entry, target, ts)) return false;
        const ring = entry.node.querySelector('.cue-ring');
        const rect = ring
            ? positionRingOn(ring, target, entry.anchorRef.containerId)
            : validRect(measureRect(target));
        if (!rect) return false;

        setClass(entry.node, 'cue--anchor-hovered', anchorHovered(target));

        const chevron = entry.node.querySelector('.cue-chevron');
        const pill = entry.node.querySelector('.cue-pill');
        const chevW = Math.max(18, vminPx(2.6));
        const chevH = chevW * (14 / 24);
        const chevSize = Math.max(chevW, chevH);
        const gap = Math.max(6, vminPx(0.8));
        const { pillW, pillH } = pillSize(entry, pill);
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;

        // Ornament placements in preference order; the first that dodges the
        // tip panel and skip button (both render ABOVE the cue layer) wins.
        const candidates = [
            { // above, chevron pointing down at the target
                chevLeft: cx - chevW / 2, chevTop: rect.top - gap * 1.6 - chevH, chevRot: '0deg',
                pillLeft: cx - pillW / 2, pillTop: rect.top - gap * 2.1 - chevH - pillH,
            },
            { // below, pointing up
                chevLeft: cx - chevW / 2, chevTop: rect.bottom + gap * 1.6, chevRot: '180deg',
                pillLeft: cx - pillW / 2, pillTop: rect.bottom + gap * 2.1 + chevH,
            },
            { // left side, pointing right
                chevLeft: rect.left - gap * 1.6 - chevSize, chevTop: cy - chevH / 2, chevRot: '-90deg',
                pillLeft: rect.left - gap * 2.2 - chevSize - pillW, pillTop: cy - pillH / 2,
            },
            { // right side, pointing left
                chevLeft: rect.right + gap * 1.6, chevTop: cy - chevH / 2, chevRot: '90deg',
                pillLeft: rect.right + gap * 2.2 + chevSize, pillTop: cy - pillH / 2,
            },
        ];
        // Prefer pointing down from above unless the target hugs the top edge.
        if (rect.top < window.innerHeight * 0.28) candidates.push(candidates.shift());

        const blockers = ornamentBlockers();
        const boxFor = (c) => ({
                left: Math.min(c.chevLeft, c.pillLeft),
                top: Math.min(c.chevTop, pill ? c.pillTop : c.chevTop),
                right: Math.max(c.chevLeft + chevW, c.pillLeft + pillW),
                bottom: Math.max(c.chevTop + chevH, pill ? c.pillTop + pillH : c.chevTop + chevH),
            });
        const fits = (c) => {
            const box = boxFor(c);
            // The safe area, not the raw viewport: a cue tucked 4px from the
            // left edge is under the notch on a landscape iPhone.
            const safe = safeBox(uiPx(4));
            if (box.left < safe.left || box.top < safe.top
                || box.right > safe.right || box.bottom > safe.bottom) {
                return false;
            }
            return !blockers.some((blocker) => rectsIntersect(blocker, box));
        };
        const collisionFree = candidates.find(fits);
        const placement = collisionFree || candidates[0];

        if (chevron) {
            setBox(chevron, placement.chevLeft, placement.chevTop, chevW, chevH);
            // Rotation rides a CSS var so the bob keyframes own transform.
            chevron.style.setProperty('--cue-chevron-rot', placement.chevRot);
        }
        if (pill) {
            // If every slot is occupied, keep the ring/chevron but suppress the
            // redundant word for this frame. Overlapping instructional copy is
            // worse than one unambiguous Tap label shared by adjacent targets.
            pill.style.display = collisionFree ? '' : 'none';
            if (collisionFree) {
                pill.style.left = `${placement.pillLeft}px`;
                pill.style.top = `${placement.pillTop}px`;
            }
        }
        const reserved = collisionFree
            ? boxFor(placement)
            : {
                left: placement.chevLeft,
                top: placement.chevTop,
                right: placement.chevLeft + chevW,
                bottom: placement.chevTop + chevH,
            };
        frameOrnamentBoxes.push(reserved);
        return true;
    }

    /* The mulligan's own words are a blocker, and the blocker is the INK rather
       than the element box. The two CLICK pills above the cards were landing on
       the subtitle's last line — 2.6px into the descenders on a desktop, and
       10-12px at every landscape phone page box, where the sentence wraps to
       three lines and the pill is drawn ~42px above the card, i.e. inside the
       header. A larger gap cannot fix that, because shortening the header moves
       the cards, and their pills, up with it.

       Box rects would over-block: the element carries half a line of leading
       below the glyphs, so a pill tucked into that leading reads as clean and
       would be rejected anyway. Range rects are one tight box per rendered
       line, which is exactly what the player can see. `fits` already prefers
       another placement and hides the pill outright if none is free, so the
       worst case here is the ring without its word, which this file already
       argues is better than overlapping copy. */
    function textInkBlockers() {
        if (!document.querySelector('#mulligan-overlay.active')) return [];
        const rects = [];
        for (const selector of ['.mulligan-header h2', '.mulligan-header p']) {
            const el = document.querySelector(selector);
            const node = el && el.firstChild;
            if (!node || node.nodeType !== Node.TEXT_NODE) continue;
            const range = document.createRange();
            range.selectNodeContents(node);
            for (const rect of range.getClientRects()) {
                if (rect.width && rect.height) rects.push(rect);
            }
        }
        return rects;
    }

    function ornamentBlockers() {
        const surfaces = ['.tutorial-tip', '#skip-tutorial-btn'].flatMap((selector) => {
            const el = document.querySelector(selector);
            if (!el) return [];
            const rect = el.getBoundingClientRect();
            return (rect.width || rect.height) ? [rect] : [];
        });
        return surfaces.concat(textInkBlockers(), frameOrnamentBoxes);
    }

    function rectsIntersect(a, b) {
        return a.left < b.right && a.right > b.left
            && a.top < b.bottom && a.bottom > b.top;
    }

    function positionLookCue(entry, ts) {
        const target = resolveAnchor(entry.anchorRef);
        if (!target) return false;
        if (!anchorReady(entry, target, ts)) return false;
        const ring = entry.node.querySelector('.cue-ring');
        return Boolean(positionRingOn(ring, target, entry.anchorRef.containerId));
    }

    function dragPathPoints(sourceRect, targetRect) {
        const sx = sourceRect.left + sourceRect.width / 2;
        const sy = sourceRect.top + sourceRect.height / 2;
        const tx = targetRect.left + targetRect.width / 2;
        const ty = targetRect.top + targetRect.height / 2;
        const mx = (sx + tx) / 2;
        const my = (sy + ty) / 2;
        // Bow the path perpendicular to the line so the comet arcs like a throw.
        const dx = tx - sx;
        const dy = ty - sy;
        const dist = Math.hypot(dx, dy) || 1;
        const bow = Math.min(dist * 0.18, vminPx(9));
        const cx = mx - (dy / dist) * bow;
        const cy = my + (dx / dist) * bow;
        return { sx, sy, tx, ty, cx, cy, dist };
    }

    function quadPoint(p, t) {
        const mt = 1 - t;
        return {
            x: mt * mt * p.sx + 2 * mt * t * p.cx + t * t * p.tx,
            y: mt * mt * p.sy + 2 * mt * t * p.cy + t * t * p.ty,
        };
    }

    function easeInOut(t) {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    function positionDragCue(entry, timestamp) {
        const source = resolveAnchor(entry.anchorRef);
        const target = resolveAnchor(entry.targetRef);
        if (!source || !target) return false;
        // BOTH ends: a drag cue draws a path between them, so either one
        // missing puts half the ornament over nothing.
        if (!anchorReady(entry, source, timestamp) || !anchorReady(entry, target, timestamp)) {
            return false;
        }

        const sourceRing = entry.node.querySelector('.cue-ring--source');
        const targetRing = entry.node.querySelector('.cue-ring--target');
        const sourceRect = positionRingOn(sourceRing, source, entry.anchorRef.containerId);
        const targetRect = targetRing
            ? positionRingOn(targetRing, target, entry.targetRef.containerId)
            : validRect(measureRect(target));
        if (!sourceRect || !targetRect) return false;

        setClass(entry.node, 'cue--anchor-hovered', anchorHovered(source));

        const svg = entry.node.querySelector('svg.cue-path');
        const trail = entry.node.querySelector('.cue-trail');
        const comet = entry.node.querySelector('.cue-comet');
        const pill = entry.node.querySelector('.cue-pill');
        const points = dragPathPoints(sourceRect, targetRect);

        svg.setAttribute('viewBox', `0 0 ${window.innerWidth} ${window.innerHeight}`);
        setBox(svg, 0, 0, window.innerWidth, window.innerHeight);
        trail.setAttribute('d', `M ${points.sx} ${points.sy} Q ${points.cx} ${points.cy} ${points.tx} ${points.ty}`);

        const isStatic = cueLayer.classList.contains('cue-layer--static');
        if (isStatic) {
            // Reduced motion: show the full guide path instead of a moving comet.
            trail.style.strokeDasharray = '';
            trail.style.strokeDashoffset = '';
        } else {
            if (!entry.startedAt) entry.startedAt = timestamp;
            const t = easeInOut(((timestamp - entry.startedAt) % COMET_PERIOD_MS) / COMET_PERIOD_MS);
            const head = quadPoint(points, t);
            const ahead = quadPoint(points, Math.min(1, t + 0.02));
            const angle = Math.atan2(ahead.y - head.y, ahead.x - head.x) * (180 / Math.PI);
            const fade = Math.min(1, Math.min(t, 1 - t) * 6);
            comet.style.transform = `translate(${head.x.toFixed(1)}px, ${head.y.toFixed(1)}px) translate(-50%, -50%) rotate(${angle.toFixed(1)}deg)`;
            comet.style.opacity = String(0.25 + fade * 0.75);

            // Trail: a dash window that follows the comet head along the path.
            const pathLength = safePathLength(trail, points.dist);
            const trailLen = pathLength * COMET_TRAIL_FRACTION;
            const headLen = pathLength * t;
            trail.style.strokeDasharray = `${Math.min(trailLen, headLen)} ${pathLength + trailLen}`;
            trail.style.strokeDashoffset = String(-(Math.max(0, headLen - trailLen)));
        }

        if (pill) {
            const mid = quadPoint(points, 0.5);
            const { pillW, pillH } = pillSize(entry, pill);
            const gap = Math.max(8, vminPx(1.2));
            const candidates = [
                { left: mid.x - pillW / 2, top: mid.y - pillH * 1.6 },
                { left: mid.x - pillW / 2, top: mid.y + pillH * 0.6 },
                { left: mid.x - pillW - gap * 1.5, top: mid.y - pillH / 2 },
                { left: mid.x + gap * 1.5, top: mid.y - pillH / 2 },
                { left: mid.x - pillW / 2, top: mid.y - pillH * 2.8 },
                { left: mid.x - pillW / 2, top: mid.y + pillH * 1.8 },
            ];
            const blockers = ornamentBlockers();
            const placement = candidates.find((candidate) => {
                const box = {
                    left: candidate.left,
                    top: candidate.top,
                    right: candidate.left + pillW,
                    bottom: candidate.top + pillH,
                };
                const safe = safeBox(uiPx(8));
                return box.left >= safe.left
                    && box.top >= safe.top
                    && box.right <= safe.right
                    && box.bottom <= safe.bottom
                    && !blockers.some((blocker) => rectsIntersect(blocker, box));
            });
            pill.style.display = placement ? '' : 'none';
            if (placement) {
                pill.style.left = `${placement.left}px`;
                pill.style.top = `${placement.top}px`;
                frameOrnamentBoxes.push({
                    left: placement.left,
                    top: placement.top,
                    right: placement.left + pillW,
                    bottom: placement.top + pillH,
                });
            }
        }
        return true;
    }

    function safePathLength(pathEl, fallback) {
        try {
            const length = pathEl.getTotalLength();
            return Number.isFinite(length) && length > 0 ? length : fallback;
        } catch (err) {
            return fallback;
        }
    }

    /* A CLASS WRITE IS A MUTATION EVEN WHEN IT CHANGES NOTHING.
       `classList.add` of a class already present, `remove` of one absent and
       `toggle(name, sameState)` all rewrite the attribute, and this loop runs
       every frame: it queued ~8 no-op writes a frame (the veil's svg and
       <body>), each waking every body-wide MutationObserver — the Skip
       button's re-seat in tutorial.js forces a layout of the board per run.
       Measured 25 September 2026 on the phone seat: ~900 ms of main thread in
       the tutorial's first seconds. Write only a change. */
    function setClass(element, name, on) {
        if (!element || element.classList.contains(name) === Boolean(on)) return;
        element.classList.toggle(name, Boolean(on));
    }

    function updateVeil(ts) {
        if (!veilSvg) return;
        const retract = () => {
            setClass(veilSvg, 'cue-veil--active', false);
            setClass(veilSvg, 'cue-veil--suspended', false);
            setClass(veilSvg, 'cue-veil--soften', false);
            setClass(document.body, 'tutorial-veil-active', false);
        };
        if (!veilActive) {
            retract();
            return;
        }

        const pad = Math.max(10, vminPx(1.6));
        const at = typeof ts === 'number' ? ts : now();
        const rects = [];
        veilAnchors.forEach((ref) => {
            const el = resolveAnchor(ref);
            if (!el) return;
            /* THE SPOTLIGHT WAITS FOR THE CARD, exactly as the ring does.
               `anchorPainted` was written for the rings after "in the beginning
               the cards to play are 'highlighted' but it takes ~1s to actually
               draw em, until then empty space is highlighted" — and the veil,
               which cuts the lit hole the player actually reads as the
               spotlight, was never given it. `FX.drawCard` holds each arriving
               hand card at `visibility: hidden`, so the hole was cut around a
               seat with nothing in it. Same 1500ms cap as the rings: a warm-up
               may delay the tutorial, never delete it. */
            if (!anchorPainted(el) && (at - veilAppliedAt) < ANCHOR_PAINT_CAP_MS) return;
            const rect = measureRect(el);
            if (!rect.width && !rect.height) return;
            rects.push(rect);
        });

        /* An unbroken scrim is worse than no scrim: it dims the whole scene and
           points at nothing. Until the first anchor is ready the veil stays
           down entirely, and comes up WITH its hole. */
        if (rects.length === 0) {
            while (veilHoles.length) veilHoles.pop().remove();
            retract();
            return;
        }

        setClass(veilSvg, 'cue-veil--active', true);
        setClass(document.body, 'tutorial-veil-active', true);

        while (veilHoles.length < rects.length) {
            const hole = document.createElementNS(SVG_NS, 'rect');
            hole.setAttribute('class', 'cue-veil-hole');
            hole.setAttribute('fill', 'url(#cue-veil-hole-gradient)');
            veilMask.appendChild(hole);
            veilHoles.push(hole);
        }
        while (veilHoles.length > rects.length) {
            const hole = veilHoles.pop();
            hole.remove();
        }

        rects.forEach((rect, i) => {
            const hole = veilHoles[i];
            const attrs = {
                x: (rect.left - pad).toFixed(1),
                y: (rect.top - pad).toFixed(1),
                width: (rect.width + pad * 2).toFixed(1),
                height: (rect.height + pad * 2).toFixed(1),
                rx: Math.max(10, vminPx(1.4)).toFixed(1),
            };
            Object.entries(attrs).forEach(([name, value]) => {
                // Dirty-check: mask edits force a re-rasterize, skip no-op writes.
                if (hole.getAttribute(name) !== value) hole.setAttribute(name, value);
            });
        });
    }

    function positionAll(timestamp) {
        if (!cueLayer) return;
        frameRects.clear();
        framePaint.clear();
        frameOrnamentBoxes.length = 0;

        // A retracting veil must never be left behind by the suspend gate —
        // deactivate it before anything can early-return.
        if (!veilActive) updateVeil();

        /* An Action Intent AWAITING THE PLAYER'S PICK is not an action in
           flight — it is the board waiting on a human, and in the tutorial the
           cue is the only thing telling that human which offer is open. The
           server holds `actionInProgress` for the WHOLE pending window, so
           without this exception the cue that teaches the step would be frozen
           and faded for exactly as long as the step lasts.
           Narrow on purpose: the class is set only while the dialog is showing
           its options (the generating beat stays suspended, like any other
           server round-trip), and only by the tutorial. */
        const awaitingIntentChoice = document.body.classList.contains('tutorial-intent-choice');
        // While a drag or a server action is in flight, the cues freeze and
        // CSS fades them; recomputing mid-drag would fight card-drag's rAF.
        const suspended = document.body.classList.contains('card-dragging')
            || document.body.classList.contains('drop-settling')
            || (Boolean(GameState?.get?.()?.actionInProgress) && !awaitingIntentChoice)
            || document.getElementById('mulligan-overlay')
                ?.classList.contains('mulligan-card-inspection-active');
        setClass(cueLayer, 'cue-layer--suspended', suspended && (cueMap.size > 0 || veilActive));
        setClass(veilSvg, 'cue-veil--suspended', suspended && veilActive);
        if (suspended) return;

        const ts = typeof timestamp === 'number' ? timestamp : now();
        let anyAnchorHovered = false;
        cueMap.forEach((entry) => {
            let ok = false;
            if (entry.spec.type === 'click') ok = positionClickCue(entry, ts);
            else if (entry.spec.type === 'look') ok = positionLookCue(entry, ts);
            else if (entry.spec.type === 'drag') ok = positionDragCue(entry, ts);
            setClass(entry.node, 'cue--hidden', !ok);
            if (entry.node.classList.contains('cue--anchor-hovered')) anyAnchorHovered = true;
            if (!entry.node.classList.contains('cue--insist') && ts - entry.appliedAt > INSIST_AFTER_MS) {
                entry.node.classList.add('cue--insist');
            }
        });

        // Hover-enlarged cards render UNDER the veil (their z 9500 is trapped
        // in lower stacking contexts), so soften the darkness while the
        // player inspects a cued card.
        setClass(veilSvg, 'cue-veil--soften', anyAnchorHovered);
        // `ts` is this frame's clock, already resolved above — the readiness cap
        // must not re-read it and drift by a frame.
        updateVeil(ts);
    }

    function tick(timestamp) {
        rafHandle = 0;
        if (!cueLayer || (cueMap.size === 0 && !veilActive)) return;
        positionAll(timestamp);
        scheduleTick();
    }

    function scheduleTick() {
        if (rafHandle) return;
        const raf = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (cb) => setTimeout(() => cb(now()), 16);
        rafHandle = raf(tick);
    }

    function cancelTick() {
        if (!rafHandle) return;
        if (typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(rafHandle);
        } else {
            clearTimeout(rafHandle);
        }
        rafHandle = 0;
    }

    /**
     * Declarative entry point. Diffs against the current cue set by stable
     * anchor identity so re-applies after board renders never rebuild DOM
     * (rebuilding would visibly restart the breathing animations).
     *
     * spec = {
     *   cues: [{ type: 'click'|'look', anchor: Element, pill?: false }
     *        | { type: 'drag', from: Element, to: Element }],
     *   veil: Element[] | null
     * }
     */
    function apply(spec = {}) {
        const cueSpecs = (spec.cues || []).filter((c) => c && (c.anchor || (c.from && c.to)));
        const veilEls = Array.isArray(spec.veil) ? spec.veil.filter(Boolean) : [];

        if (cueSpecs.length === 0 && veilEls.length === 0 && !cueLayer) return;
        ensureLayers();

        const nextKeys = new Set();
        const ts = now();
        cueSpecs.forEach((cueSpec) => {
            const key = cueKey(cueSpec);
            nextKeys.add(key);
            let entry = cueMap.get(key);
            if (!entry) {
                entry = {
                    spec: cueSpec,
                    node: buildCueNode(cueSpec),
                    appliedAt: ts,
                    startedAt: 0,
                };
                cueLayer.appendChild(entry.node);
                cueMap.set(key, entry);
            }
            entry.spec = cueSpec;
            syncInteractionLabel(entry);
            if (cueSpec.type === 'drag') {
                entry.anchorRef = makeAnchorRef(cueSpec.from);
                entry.targetRef = makeAnchorRef(cueSpec.to);
            } else {
                entry.anchorRef = makeAnchorRef(cueSpec.anchor);
            }
        });

        cueMap.forEach((entry, key) => {
            if (!nextKeys.has(key)) {
                entry.node.remove();
                cueMap.delete(key);
            }
        });

        const nextVeilKey = veilEls.map((el) => anchorKey(el)).join('|');
        if (!veilActive || nextVeilKey !== veilKey) veilAppliedAt = ts;
        veilKey = nextVeilKey;
        veilActive = veilEls.length > 0;
        veilAnchors = veilEls.map(makeAnchorRef);

        positionAll();
        if (cueMap.size > 0 || veilActive) {
            scheduleTick();
        } else {
            // Nothing left to render or track: drop every lingering state
            // class NOW — the loop is about to stop, so no later frame can.
            cueLayer.classList.remove('cue-layer--suspended');
            if (veilSvg) {
                veilSvg.classList.remove('cue-veil--suspended');
                veilSvg.classList.remove('cue-veil--soften');
            }
            cancelTick();
        }
    }

    function clear() {
        apply({ cues: [], veil: null });
    }

    function refresh() {
        positionAll();
    }

    function destroy() {
        cancelTick();
        cueMap.clear();
        veilAnchors = [];
        veilKey = '';
        veilAppliedAt = 0;
        veilHoles = [];
        veilActive = false;
        document.body.classList.remove('tutorial-veil-active');
        if (resizeHandler) {
            window.removeEventListener('resize', resizeHandler);
            window.removeEventListener('moonfall:viewport-change', resizeHandler);
            resizeHandler = null;
        }
        if (localizationHandler) {
            window.removeEventListener('moonfall:languagechange', localizationHandler);
            document.removeEventListener('moonfall:i18n-translated', localizationHandler);
            localizationHandler = null;
        }
        if (motionQuery && motionHandler && typeof motionQuery.removeEventListener === 'function') {
            motionQuery.removeEventListener('change', motionHandler);
        }
        motionQuery = null;
        motionHandler = null;
        if (cueLayer) {
            cueLayer.remove();
            cueLayer = null;
        }
        if (veilLayer) {
            veilLayer.remove();
            veilLayer = null;
            veilSvg = null;
            veilMask = null;
        }
    }

    function isActive() {
        return cueMap.size > 0;
    }

    return {
        apply,
        clear,
        refresh,
        destroy,
        isActive,
    };
})();

export { TutorialCues };
